import { enrichSpellActionData } from './combat/SpellActionReference';

const ABILITY_LABELS = Object.freeze({
  intelligence: 'Intelligence',
  wisdom: 'Wisdom',
  charisma: 'Charisma'
});

const asNumber = (value, fallback = null) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const signed = (value) => {
  const numeric = asNumber(value, 0);
  return numeric >= 0 ? `+${numeric}` : String(numeric);
};

const formatDamage = (damage) => (Array.isArray(damage) ? damage : [damage])
  .filter(Boolean)
  .map((entry) => typeof entry === 'string'
    ? entry
    : `${entry?.dice || entry?.formula || ''} ${entry?.type || entry?.damageType || ''}`.trim())
  .filter(Boolean)
  .join(', ');

const formatComponents = (spell) => {
  if (typeof spell?.components === 'string' && spell.components.trim()) return spell.components.trim();
  return [spell?.verbal ? 'V' : '', spell?.somatic ? 'S' : '', spell?.material || spell?.materialComponent ? 'M' : '']
    .filter(Boolean)
    .join(', ');
};

const getAbilityScore = (subject, ability) => {
  const aliases = ability === 'intelligence' ? ['intelligence', 'int']
    : ability === 'wisdom' ? ['wisdom', 'wis']
      : ['charisma', 'cha'];
  for (const branch of [subject, subject?.data, subject?.schemaData, subject?.canonicalData].filter(Boolean)) {
    for (const alias of aliases) {
      const score = asNumber(branch?.abilities?.[alias]);
      if (score != null) return score;
    }
  }
  return null;
};

const getProficiencyBonus = (subject) => {
  const explicit = asNumber(subject?.proficiencyBonus ?? subject?.combatStats?.proficiencyBonus ?? subject?.data?.combatStats?.proficiencyBonus);
  if (explicit != null) return explicit;
  const level = Math.max(1, asNumber(subject?.level, 1));
  return Math.floor((level - 1) / 4) + 2;
};

const buildSpell = (entry, index, subject) => {
  const spell = enrichSpellActionData(entry, { subject }) || {};
  const level = Math.max(0, Math.min(9, asNumber(spell.level ?? spell.spellLevel, 0)));
  const name = String(spell.name || spell.title || spell.label || '').trim();
  if (!name) return null;
  const mechanics = [
    spell.castingTime ? { label: 'Time', value: spell.castingTime } : null,
    spell.range ? { label: 'Range', value: spell.range } : null,
    formatComponents(spell) ? { label: 'Components', value: formatComponents(spell) } : null,
    spell.duration ? { label: 'Duration', value: spell.duration } : null,
    formatDamage(spell.damage) ? { label: 'Damage', value: formatDamage(spell.damage) } : null,
    spell.attackBonus != null ? { label: 'Attack', value: signed(spell.attackBonus) } : null,
    spell.saveType ? { label: 'Save', value: String(spell.saveType).replace(/^./, (letter) => letter.toUpperCase()) } : null
  ].filter(Boolean);
  const tags = [
    level === 0 ? 'Cantrip' : `Level ${level}`,
    level > 0 && spell.prepared === true ? 'Prepared' : '',
    spell.ritual === true ? 'Ritual' : '',
    spell.concentration === true ? 'Concentration' : ''
  ].filter(Boolean);

  return {
    key: String(spell.id || `spell-${level}-${index}-${name.toLowerCase()}`),
    name,
    level,
    levelLabel: level === 0 ? 'Cantrips' : `Level ${level}`,
    tags,
    mechanics,
    description: String(spell.description || spell.text || spell.summary || spell.effect || '').trim(),
    material: String(spell.material || spell.materialComponent || '').trim(),
    modification: String(spell.modification || '').trim(),
    acquisition: String(spell.acquisition || spell.grantedBy || spell.source?.name || spell.source || '').trim()
  };
};

export const buildCharacterSpellbookViewModel = (spellData = {}, subject = null) => {
  const ability = String(spellData?.ability || '').trim().toLowerCase();
  const score = ability ? getAbilityScore(subject, ability) : null;
  const modifier = score == null ? null : Math.floor((score - 10) / 2);
  const proficiencyBonus = getProficiencyBonus(subject);
  const attackBonus = asNumber(spellData?.attackBonus, modifier == null ? null : modifier + proficiencyBonus);
  const saveDC = asNumber(spellData?.saveDC, modifier == null ? null : 8 + modifier + proficiencyBonus);
  const slots = Object.entries(spellData?.slots || {})
    .map(([level, slot]) => ({
      level: Number(level),
      total: Math.max(0, asNumber(slot?.total ?? slot?.max, 0)),
      used: Math.max(0, asNumber(slot?.used, 0))
    }))
    .filter((slot) => slot.level >= 1 && slot.level <= 9 && (slot.total > 0 || slot.used > 0))
    .sort((left, right) => left.level - right.level)
    .map((slot) => ({ ...slot, remaining: Math.max(0, slot.total - slot.used) }));
  const spells = (Array.isArray(spellData?.entries) ? spellData.entries : [])
    .map((entry, index) => buildSpell(entry, index, subject))
    .filter(Boolean)
    .sort((left, right) => left.level - right.level || left.name.localeCompare(right.name));
  const groups = Array.from({ length: 10 }, (_, level) => ({
    level,
    title: level === 0 ? 'Cantrips' : `Level ${level}`,
    spells: spells.filter((spell) => spell.level === level)
  })).filter((group) => group.spells.length > 0);

  return {
    ability: ABILITY_LABELS[ability] || (ability ? ability.replace(/^./, (letter) => letter.toUpperCase()) : ''),
    modifier,
    attackBonus,
    saveDC,
    slots,
    groups,
    spellCount: spells.length,
    hasContent: groups.length > 0 || slots.length > 0 || Boolean(ability)
  };
};

export default buildCharacterSpellbookViewModel;
